import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "sfs_pwa_dismissed_until";
const DISMISS_DAYS = 7;

export default function InstallPrompt() {
  const [evt, setEvt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    // Don't show if user dismissed recently
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (until && Date.now() < until) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setEvt(e);
      // Delay 6s so users see content first
      setTimeout(() => setShow(true), 6000);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => setShow(false));
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!show || !evt) return null;

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000)
    );
  };

  const install = async () => {
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      if (choice.outcome !== "accepted") dismiss();
      setShow(false);
    } catch {
      dismiss();
    }
  };

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-5 sm:bottom-24 sm:max-w-sm z-[60] animate-fade-up"
    >
      <div className="card-soft p-4 sm:p-5 border-2 border-brand-gold/40 bg-white/95 backdrop-blur-md">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-forest text-brand-gold">
            <Smartphone className="h-5 w-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-outfit font-bold text-brand-forest text-base">
              Install Swift Financial
            </div>
            <p className="mt-1 text-sm text-brand-sage leading-snug">
              Add to your home screen for one-tap access to inquiries, EMI calculator and daily news.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="install-prompt-accept"
                onClick={install}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-terracotta px-4 py-2 text-xs font-semibold text-white hover:bg-[#b25535] transition-colors"
              >
                <Download className="h-3.5 w-3.5" /> Install
              </button>
              <button
                data-testid="install-prompt-dismiss"
                onClick={dismiss}
                className="text-xs font-semibold text-brand-sage hover:text-brand-forest transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            aria-label="Close"
            onClick={dismiss}
            className="text-brand-sage hover:text-brand-forest transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
